import {
  isDir,
  resolve,
  type Fs,
} from "@/components/heroes/terminal/terminal-fs";
import { KNOWN_COMMANDS } from "@/components/heroes/terminal/terminal-commands";

/**
 * Tab-completion for the interactive shell. Pure (no React, no
 * state) — `completeInput` takes the current input line, the
 * caret offset within it, and the fs; returns either a
 * substitution (the new input line with the completed token in
 * place) or a list of options for the caller to render as help
 * (like `ls` does for an ambiguous prefix).
 *
 * Completion is context-aware:
 *   - the first whitespace-separated token is the command (and
 *     completes against `KNOWN_COMMANDS`);
 *   - later tokens are arguments. For commands that take
 *     filesystem paths (`cat`, `cd`, `ls`), arguments complete
 *     against the fake fs; for everything else Tab is a no-op
 *     so it doesn't surprise the user (e.g. `echo` arguments
 *     are free-form text).
 */

/** Commands whose arguments should resolve against the
 *  filesystem (so Tab completes file/folder names). */
const PATH_TAKING_COMMANDS: ReadonlySet<string> = new Set([
  "bash",
  "cat",
  "cd",
  "ls",
  "sh",
]);

/** Result of a completion attempt. `complete` is the new input
 *  line the user should see (already substituted). `options` is
 *  the list of possible completions the user can choose from;
 *  non-empty when there are multiple matches and we couldn't
 *  unambiguously complete the prefix. */
export type CompletionResult = {
  complete: string;
  options: string[];
};

/** Find the longest common prefix of a set of strings. Empty
 *  input → empty string. A single string → that string. */
function longestCommonPrefix(strs: string[]): string {
  if (strs.length === 0) return "";
  if (strs.length === 1) return strs[0];
  let prefix = strs[0];
  for (let i = 1; i < strs.length && prefix.length > 0; i++) {
    const s = strs[i];
    while (!s.startsWith(prefix) && prefix.length > 0) {
      prefix = prefix.slice(0, -1);
    }
  }
  return prefix;
}

/** Split a path string into a parent directory + the trailing
 *  segment the user is typing. Mirrors how shells split a partial
 *  path for completion: everything up to and including the last
 *  `/` is the parent dir, whatever follows is the prefix we're
 *  matching against. Returns `[parent, prefix]`. If the path is
 *  empty or contains no `/`, the parent is `""` (cwd). */
function splitPartialPath(p: string): { parent: string; prefix: string } {
  const slash = p.lastIndexOf("/");
  if (slash === -1) return { parent: "", prefix: p };
  return { parent: p.slice(0, slash + 1), prefix: p.slice(slash + 1) };
}

/** Try to complete the current input. `inputLine` is the entire
 *  current input, `cursorIdx` is the index of the caret within
 *  that line. Returns `null` if there is nothing to complete (e.g.
 *  no token under the cursor, or no matches at all). */
export function completeInput(
  inputLine: string,
  cursorIdx: number,
  cwd: string,
  fs: Fs
): CompletionResult | null {
  // Find the boundaries of the current token: the cursor sits
  // somewhere inside it. Walk back from the cursor to the last
  // whitespace, and forward to the next whitespace (so we handle
  // completion in the middle of a line too, not just at the end).
  let start = cursorIdx;
  while (start > 0 && !/\s/.test(inputLine[start - 1])) start--;
  let end = cursorIdx;
  while (end < inputLine.length && !/\s/.test(inputLine[end])) end++;
  const token = inputLine.slice(start, end);
  const before = inputLine.slice(0, start);
  const after = inputLine.slice(end);

  // Identify what role this token plays in the command line. We
  // walk from the start of the line forward, skipping leading
  // whitespace, and the first non-whitespace run is the command.
  // Anything else is an argument. This also handles `./contact.sh`
  // and `bash contact.sh` style invocations — they're treated as
  // the "command" token, not as a path.
  const cmdMatch = inputLine.match(/^\s*(\S+)/);
  const cmd = cmdMatch ? cmdMatch[1] : "";
  const isFirstToken = start === (inputLine.match(/^\s*/)?.[0].length ?? 0);
  const tokenIsPathLike =
    token.startsWith("/") ||
    token.startsWith("./") ||
    token.startsWith("../") ||
    token.startsWith("~/") ||
    token.includes("/");

  // Decide what kind of completion this is. A first token is a
  // command name (completed against `KNOWN_COMMANDS`) unless it
  // looks like a path, as when running a script: `./con<Tab>`
  // completes to `./contact.sh`. For argument tokens, we route to
  // path completion only when the command actually takes paths;
  // otherwise we no-op so Tab doesn't surprise the user (e.g.
  // `echo` arguments are free-form text).
  let kind: "command" | "path";
  if (isFirstToken && !tokenIsPathLike) {
    kind = "command";
  } else if (PATH_TAKING_COMMANDS.has(cmd) || tokenIsPathLike) {
    // Path completion when the command takes paths OR the user
    // is typing a path-like token (covers `cat /etc/foo` where
    // `cmd` would be `cat` and we route to path, but also `cat
    // ~/<Tab>` where the `~/` prefix immediately tells us this
    // is a path even if `cmd` lookup somehow missed it).
    kind = "path";
  } else {
    return null;
  }

  let candidates: string[] = [];
  let displayBase = ""; // path prefix to keep displayed for the user
  let pathPrefix = ""; // what to prepend to each candidate to form the new token

  if (kind === "command") {
    candidates = KNOWN_COMMANDS.filter((c) => c.startsWith(token));
  } else {
    // Path completion. Split the partial path into a parent dir
    // (already-typed prefix ending in `/`) and the trailing
    // segment (the actual prefix we're matching). Resolve the
    // parent against cwd, then look up matching children.
    const { parent, prefix } = splitPartialPath(token);
    pathPrefix = parent;
    // Resolve the parent directory against cwd. `parent` may be
    // empty (cwd), absolute (`/home/emily/...` or `~/...`), or
    // relative, and may contain `..` (e.g. `../blog/`). A parent
    // outside home has nothing to complete.
    const parentPath = resolve(parent === "" ? "." : parent, cwd);
    if (parentPath === null) return null;
    const dir = fs[parentPath];
    if (!isDir(dir)) return null;
    const entries = Object.keys(dir.children).sort();
    if (entries.length === 0) return null;
    candidates = entries.filter((name) => name.startsWith(prefix));
    // What we display as the "base" of the completion list —
    // include the parent so the user can see where the entries
    // are coming from.
    const parentDisplay = parentPath === "~" ? "~" : parentPath;
    displayBase = parentDisplay;
  }

  if (candidates.length === 0) return null;

  if (candidates.length === 1) {
    // Unique match — append the full name. If it's a directory
    // (in the path case), add a trailing `/` so the user can
    // continue tab-completing into it. The display token includes
    // `pathPrefix` so partial-path completion works (e.g. typing
    // `cat blog/` and tab-completing a file gets `blog/post.md`,
    // not just `post.md`).
    const only = candidates[0];
    let newToken = pathPrefix + only;
    if (kind === "path") {
      const target = resolve(newToken, cwd);
      if (target !== null && isDir(fs[target])) newToken += "/";
    }
    return {
      complete: before + newToken + after,
      options: [
        displayBase +
          (displayBase.endsWith("/") || displayBase === "~" ? "" : "/") +
          candidates[0],
      ],
    };
  }

  // Multiple matches — complete to the longest common prefix,
  // and return the full list so the caller can render it as
  // help output (like `ls`).
  const common = longestCommonPrefix(candidates);
  let newToken = common;
  if (newToken === token) {
    // Nothing more to complete — the user has typed as far as the
    // common prefix goes. Return the options so the caller can
    // print them.
    return {
      complete: inputLine,
      options: candidates.map(
        (c) =>
          displayBase +
          (displayBase.endsWith("/") || displayBase === "~" ? "" : "/") +
          c
      ),
    };
  }
  if (kind === "path") newToken = pathPrefix + newToken;
  return {
    complete: before + newToken + after,
    options: candidates.map(
      (c) =>
        displayBase +
        (displayBase.endsWith("/") || displayBase === "~" ? "" : "/") +
        c
    ),
  };
}
